<template>
  <!-- Table Container Card -->
  <b-card id="page-products" no-body class="mb-0 p-2">
    <b-card-header
      class="pl-0 pr-0 d-flex align-items-start justify-content-start"
    >
      <b-col cols="12" md="4">
        <b-card-title
          ><h2>{{ $t("pages.qr.title") }}</h2></b-card-title
        >
      </b-col>
    </b-card-header>

    <b-card-body>
      <b-row>
        <b-col cols="12">
          <template v-if="loading">
            <b-spinner variant="primary" label="Cargando QR..." />
          </template>
          <template v-else>
            <qrcode-vue
              v-if="qrData"
              :value="qrData"
              :size="300"
              class="qr-code"
              level="H"
            />
            <div v-else class="text-center qr-placeholder">
              <b-alert
                variant="danger"
                show
              >
                <div class="alert-body">
                  <strong>
                    <feather-icon
                      icon="AlertOctagonIcon"
                      size="24"
                    />
                      QR no generado
                    </strong>
                </div>
              </b-alert>
            </div>
          </template>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>
<script>
import _ from "lodash";
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCol,
  BButton,
  BTable,
  BTooltip,
  BSpinner,
  BFormSelect,
  BFormFile,
  BOverlay,
  BListGroup,
  BListGroupItem,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormGroup,
  BCardBody,
  BRow,
  BAlert,
} from "bootstrap-vue";
import QrcodeVue from "qrcode.vue";
import Ripple from "vue-ripple-directive";
import { qrBot } from "@core/api/request/qrbot";
import { createSignalRConnection } from "@core/api/request/signalr";
import {
  getParams,
  showLoading,
  stopLoading,
  getParamsAll,
} from "@core/utils/utils";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCol,
    BButton,
    BTable,
    BTooltip,
    BSpinner,
    BFormSelect,
    BFormFile,
    BOverlay,
    BListGroup,
    BListGroupItem,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormGroup,
    BRow,
    BCardBody,
    QrcodeVue,
    BAlert,
  },
  directives: {
    "b-tooltip": BTooltip,
    Ripple,
  },
  data: () => ({
    model: "qrbot",
    companyGuid: null,
    loading: false,
    qrData: null,
    connection: null,
    reconnectAttempts: 0,
    reconnectTimer: null,
    _isDestroyed: false,
    pollingInterval: null
  }),
  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      this.handleCompanyGuidChange(guid);
    },
  },
  created() {
    this.companyGuid = this.$store.state.appConfig.activeCompanyGuid;
    if (this.companyGuid) this.initSignalR();
    this.startPolling();
  },
  mounted() {
    const listContainer = document.getElementById("list-container");
    if (listContainer) {
      listContainer.addEventListener("scroll", this.handleScroll);
    }
  },
  beforeDestroy() {
    this.cleanup();
    const listContainer = document.getElementById("list-container");
    if (listContainer) {
      listContainer.removeEventListener("scroll", this.handleScroll);
    }
  },
  methods: {
    async loadData(silent = false) {
      if (!silent) showLoading();
      try {
        const resp = await qrBot(this.companyGuid);
        this.qrData = _.get(resp, "data.urlQR", null);
        console.log("QR actualizado desde API:", this.qrData);
      } catch (error) {
        console.error("API Error:", error);
        if (!silent) this.alertToast("danger", "Error", "No se pudo cargar el QR");
      } finally {
        if (!silent) stopLoading();
      }
    },
    async initSignalR() {
      if (this._isDestroyed || !this.companyGuid) return;

      try {
        const connection = createSignalRConnection("qrBotHub");
        this.connection = connection;

        connection.on("UpdateQRCode", async (newUrl) => {
          if (!this._isDestroyed && connection === this.connection) {
            this.handleQrUpdate(newUrl);
          }
        });

        connection.onreconnected(async () => {
          if (this._isDestroyed || connection !== this.connection) return;
          await this.joinCurrentCompanyGroup(connection);
        });

        connection.onclose(async () => {
          if (!this._isDestroyed && connection === this.connection) {
            await this.reconnect(connection);
          }
        });

        await this.startConnection(connection);
      } catch (error) {
        console.error('Error inicializando SignalR:', error);
        this.reconnect(this.connection);
      }
    },
    async startConnection(connection = this.connection) {
      if (
        !connection ||
        connection !== this.connection ||
        this._isDestroyed ||
        !this.companyGuid
      )
        return;

      if (
        connection.state === "Connected" ||
        connection.state === "Connecting" ||
        connection.state === "Reconnecting"
      )
        return;

      try {
        await connection.start();

        if (this._isDestroyed || connection !== this.connection) {
          await connection.stop();
          return;
        }
        this.reconnectAttempts = 0;
        console.log("[SignalR] Conectado. Estado:", connection.state);

        await this.joinCurrentCompanyGroup(connection);
      } catch (error) {
        if (!this._isDestroyed && connection === this.connection) {
          console.log("Error al conectar a SignalR:", error)
          await this.reconnect(connection);
        }
      }
    },
    async joinCurrentCompanyGroup(connection = this.connection) {
      if (
        !connection ||
        connection !== this.connection ||
        connection.state !== "Connected" ||
        !this.companyGuid ||
        this._isDestroyed
      )
        return;

      try {
        await connection.invoke("JoinGroup", this.companyGuid);
        if (connection === this.connection && !this._isDestroyed) {
          await this.loadData();
        }
      } catch (error) {
        console.error("Error al unirse al grupo SignalR de QR:", error);
      }
    },
    async handleCompanyGuidChange(guid) {
      this.companyGuid = guid;
      this.qrData = null;
      await this.stopSignalRConnection();
      if (!this._isDestroyed && this.companyGuid) await this.initSignalR();
    },
    async handleQrUpdate(newUrl) {
      try {
        this.qrData = null;
        await this.$nextTick();
        this.qrData = newUrl;
        await this.loadData(true);
      } catch (error) {
        console.error("Error actualizando QR:", error);
      }
    },
    async reconnect(connection = this.connection) {
      if (
        this._isDestroyed ||
        !connection ||
        connection !== this.connection ||
        !this.companyGuid
      )
        return;

      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

      if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

      this.reconnectTimer = setTimeout(async () => {
        this.reconnectTimer = null;
        if (connection === this.connection) {
          await this.startConnection(connection);
        }
      }, delay);
    },
    async stopSignalRConnection() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      const connection = this.connection;
      this.connection = null;
      if (!connection) return;

      connection.off("UpdateQRCode");
      try {
        await connection.stop();
      } catch (error) {
        console.error("Error cerrando SignalR de QR:", error);
      }
    },
    startPolling() {
      this.pollingInterval = setInterval(() => {
        if (!this._isDestroyed && this.companyGuid) this.loadData(true);
      }, 60000);
    },
    cleanup() {
      this._isDestroyed = true;
      this.stopSignalRConnection();
      if (this.pollingInterval) clearInterval(this.pollingInterval);
    },
    onInfo() {
      console.log("Displaying tutorial info");
    },
    message(title, text, icon, html) {
      this.$swal({
        title,
        text,
        html,
        icon,
        customClass: {
          confirmButton: "btn btn-success",
        },
      });
    },
    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.list-container {
  height: 60vh !important; /* Ajusta el alto según tus necesidades */
  overflow-y: auto;
}
.qr-code {
  border: 0px solid #ddd;
  padding: 10px;
  border-radius: 8px;
  margin: 20px auto;
}

.qr-placeholder {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;

  .alert {
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
  }
}
</style>
